import type {BrowserPerformanceContext} from '../../browserPerformanceContext';
import type {InputActionContext} from '../../inputActionContext';
import {
  DEFAULT_WORKFLOW_PERF_WINDOW_DURATION_MS,
  MAX_PERF_OBSERVATION_DURATION_MS,
  PERF_WINDOW_SAMPLING_VITAL_NAME,
  PERF_WINDOW_VITAL_NAME,
} from './constants';
import type {LoafObservationRuntime} from './loafObservationRuntime';
import {
  getInteractionMetrics,
  isEventTimingEntry,
  type RecordedEventTiming,
} from './performanceMetrics';
import {
  createSamplingPolicy,
  getTriggerSettings,
  observationKey,
  type SamplingPolicy,
} from './samplingPolicy';
import type {
  PerfWindowCallerContext,
  PerfWindowContext,
  PerfWindowFrameMetrics,
  PerfWindowInitiatingEvent,
  PerfWindowObservationOptions,
  PerfWindowOutcome,
  PerfWindowReason,
  PerfWindowTrigger,
  PerfWindowVitalOptions,
  RecordPerfWindowVital,
} from './types';
import {waitForPerfWindowDelivery} from './waitForPerfWindowDelivery';

const EVENT_TIMING_DURATION_THRESHOLD_MS = 16;
const MAX_RECENT_EVENT_TIMINGS = 1000;
const EVENT_TIMING_OBSERVER_OPTIONS: PerformanceObserverInit & {
  durationThreshold: number;
} = {
  type: 'event',
  buffered: true,
  durationThreshold: EVENT_TIMING_DURATION_THRESHOLD_MS,
};

interface WindowedObservation {
  key: string;
  trigger: PerfWindowTrigger;
  workflow: string;
  windowDurationMs: number;
  minStartIntervalMs: number;
  interruptOnViewChange: boolean;
  startTime: number;
  endTime: number;
  initiatingEvent?: PerfWindowInitiatingEvent;
  inputContext?: InputActionContext;
  workflowContext?: PerfWindowCallerContext;
  recordAbandoned: boolean;
  retriggerCount: number;
  collectorId: number;
  timeoutId: number;
  backgroundedAt?: number;
  cancelDelivery?: () => void;
}

export class WindowedPerfObserver {
  private eventTimingObserver: PerformanceObserver | undefined;
  private eventTimingSubscriberCount = 0;
  private readonly recentEventTimings: RecordedEventTiming[] = [];
  private readonly observations = new Map<string, WindowedObservation>();

  constructor(
    private readonly runtime: LoafObservationRuntime,
    private readonly recordVital: RecordPerfWindowVital,
    private readonly samplingPolicy: SamplingPolicy = createSamplingPolicy()
  ) {}

  // Keep browser event timings available to attach input latency to reports.
  // This observer runs independently of the perf-window and input-lag enable flags.
  initialize(): () => void {
    if (
      !this.runtime.isSupported() ||
      !this.runtime.supportsEntryType('event')
    ) {
      return () => {};
    }

    this.eventTimingSubscriberCount += 1;
    if (this.eventTimingObserver == null) {
      this.eventTimingObserver = new PerformanceObserver(list => {
        this.recordEventTimings(list.getEntries());
      });
      this.eventTimingObserver.observe(EVENT_TIMING_OBSERVER_OPTIONS);
    }

    let stopped = false;
    return () => {
      if (stopped) {
        return;
      }
      stopped = true;
      this.eventTimingSubscriberCount -= 1;
      if (this.eventTimingSubscriberCount === 0) {
        this.eventTimingObserver?.disconnect();
        this.eventTimingObserver = undefined;
        this.recentEventTimings.length = 0;
      }
    };
  }

  /**
   * Starts a timed measurement. Repeated triggers of the same trigger and workflow
   * share the current window, keeping its original end time and the first trigger's context.
   */
  observe(options: PerfWindowObservationOptions): boolean {
    const {trigger, workflow} = options;
    const settings = getTriggerSettings(trigger);
    const windowDurationMs =
      settings.windowDurationMs ??
      options.windowDurationMs ??
      DEFAULT_WORKFLOW_PERF_WINDOW_DURATION_MS;
    if (
      typeof document === 'undefined' ||
      document.visibilityState !== 'visible' ||
      !settings.enabled ||
      !this.isValid(workflow, windowDurationMs) ||
      !this.runtime.isSupported()
    ) {
      return false;
    }

    const now = performance.now();
    const key = observationKey(trigger, workflow);
    // A repeated trigger joins the current window without starting another one,
    // so the cooldown check only applies after this branch.
    const existing = this.observations.get(key);
    if (existing != null) {
      if (now < existing.endTime) {
        existing.retriggerCount += 1;
        return true;
      }
      // Keep the key occupied until pending frame timings have been delivered.
      // A late timer or another input must not replace the report still being finalized.
      this.scheduleCompletion(key);
      return false;
    }

    if (
      !this.samplingPolicy.isCooldownElapsed(
        trigger,
        workflow,
        now,
        settings.minIntervalMs
      )
    ) {
      return false;
    }
    const started = this.runtime.createCollector(windowDurationMs);
    if (started == null) {
      return false;
    }
    this.samplingPolicy.recordStart(trigger, workflow, started.startTime);
    const observation: WindowedObservation = {
      key,
      trigger,
      workflow,
      windowDurationMs,
      minStartIntervalMs: settings.minIntervalMs,
      interruptOnViewChange: settings.interruptOnViewChange,
      startTime: started.startTime,
      endTime: started.startTime + windowDurationMs,
      initiatingEvent: options.initiatingEvent,
      inputContext:
        options.inputContext == null ? undefined : {...options.inputContext},
      workflowContext:
        options.workflowContext == null
          ? undefined
          : {...options.workflowContext},
      recordAbandoned: options.recordAbandoned !== false,
      retriggerCount: 0,
      collectorId: started.collectorId,
      timeoutId: 0,
    };
    observation.timeoutId = window.setTimeout(
      () => this.scheduleCompletion(key),
      windowDurationMs
    );
    this.observations.set(key, observation);
    if (settings.recordStarted) {
      this.recordStarted(observation);
    }
    return true;
  }

  dispose(): void {
    for (const observation of this.observations.values()) {
      this.discardObservation(observation);
    }
  }

  markBackgrounded(): void {
    for (const observation of this.observations.values()) {
      observation.backgroundedAt ??= performance.now();
    }
  }

  /** Reports windows that must not cross into the next RUM view, without waiting for delivery. */
  interruptForViewChange(): void {
    const endTime = performance.now();
    for (const observation of this.observations.values()) {
      if (observation.interruptOnViewChange) {
        this.finishEarly(observation, endTime, 'interrupted', 'view-changed');
      }
    }
  }

  handlePageAbandonment(reason: 'pagehide' | 'freeze'): void {
    this.markBackgrounded();
    const endTime = performance.now();
    for (const observation of this.observations.values()) {
      if (!observation.recordAbandoned) {
        this.discardObservation(observation);
        continue;
      }
      this.finishEarly(observation, endTime, 'abandoned', reason);
    }
  }

  private isValid(workflow: string, duration: number): boolean {
    return (
      workflow.length > 0 &&
      Number.isFinite(duration) &&
      duration > 0 &&
      duration <= MAX_PERF_OBSERVATION_DURATION_MS
    );
  }

  private scheduleCompletion(key: string): void {
    const observation = this.observations.get(key);
    if (observation == null || observation.cancelDelivery != null) {
      return;
    }
    // The collector already has its fixed cutoff. This wait only allows timing
    // delivery; it does not extend the interval in which new frames are admitted.
    observation.cancelDelivery = waitForPerfWindowDelivery(deliveryComplete =>
      this.completeObservation(key, deliveryComplete)
    );
  }

  private completeObservation(key: string, deliveryComplete: boolean): void {
    const observation = this.observations.get(key);
    if (observation == null) {
      return;
    }
    this.drainPendingEventTimingEntries();
    const result = this.runtime.finalizeCollector(
      observation.collectorId,
      observation.windowDurationMs
    );
    if (result == null) {
      this.discardObservation(observation);
      return;
    }
    const {duration, measurementComplete, ...metrics} = result;
    const reason: PerfWindowReason | undefined = !deliveryComplete
      ? 'delivery-timeout'
      : !measurementComplete
        ? 'entries-dropped'
        : undefined;
    this.finishObservation(observation, {
      startTime: performance.timeOrigin + observation.startTime,
      duration,
      context: this.getContext(
        observation,
        metrics,
        'completed',
        deliveryComplete && measurementComplete,
        duration,
        reason
      ),
    });
  }

  // Stops admitting frames now, reads what the browser has queued, and reports
  // immediately. Delivery is not awaited, so the report is marked incomplete.
  private finishEarly(
    observation: WindowedObservation,
    endTime: number,
    outcome: 'abandoned' | 'interrupted',
    reason: PerfWindowReason
  ): void {
    this.runtime.truncateCollectorEnd(observation.collectorId, endTime);
    this.drainPendingEventTimingEntries();
    const duration = Math.min(
      observation.windowDurationMs,
      Math.max(0, endTime - observation.startTime)
    );
    const result = this.runtime.finalizeCollector(
      observation.collectorId,
      duration
    );
    if (result == null) {
      this.discardObservation(observation);
      return;
    }
    const {
      duration: observedDuration,
      measurementComplete: _complete,
      ...metrics
    } = result;
    this.finishObservation(observation, {
      startTime: performance.timeOrigin + observation.startTime,
      duration: observedDuration,
      context: this.getContext(
        observation,
        metrics,
        outcome,
        false,
        observedDuration,
        reason
      ),
    });
  }

  private getContext(
    observation: WindowedObservation,
    metrics: PerfWindowFrameMetrics & BrowserPerformanceContext,
    outcome: PerfWindowOutcome,
    measurementComplete: boolean,
    duration: number,
    reason: PerfWindowReason | undefined
  ): PerfWindowContext {
    return {
      trigger: observation.trigger,
      mode: 'window',
      workflow: observation.workflow,
      outcome,
      ...(reason == null ? {} : {reason}),
      backgrounded:
        observation.backgroundedAt != null &&
        observation.backgroundedAt <= observation.startTime + duration,
      measurementComplete,
      minStartIntervalMs: observation.minStartIntervalMs,
      configuredWindowDurationMs: observation.windowDurationMs,
      retriggerCount: observation.retriggerCount,
      ...(observation.workflowContext == null
        ? {}
        : {workflowContext: observation.workflowContext}),
      ...metrics,
      ...observation.inputContext,
      ...getInteractionMetrics(
        this.recentEventTimings,
        observation.initiatingEvent
      ),
    };
  }

  private recordStarted(observation: WindowedObservation): void {
    this.recordVital(PERF_WINDOW_SAMPLING_VITAL_NAME, {
      startTime: performance.timeOrigin + observation.startTime,
      duration: 0,
      context: {
        ...observation.inputContext,
        outcome: 'started',
        trigger: observation.trigger,
        mode: 'window',
        workflow: observation.workflow,
        configuredWindowDurationMs: observation.windowDurationMs,
        minStartIntervalMs: observation.minStartIntervalMs,
        ...(observation.workflowContext == null
          ? {}
          : {workflowContext: observation.workflowContext}),
      },
    });
  }

  private finishObservation(
    observation: WindowedObservation,
    vital: PerfWindowVitalOptions
  ): void {
    window.clearTimeout(observation.timeoutId);
    observation.cancelDelivery?.();
    this.observations.delete(observation.key);
    this.recordVital(PERF_WINDOW_VITAL_NAME, vital);
  }

  private discardObservation(observation: WindowedObservation): void {
    window.clearTimeout(observation.timeoutId);
    observation.cancelDelivery?.();
    this.observations.delete(observation.key);
    this.runtime.cancelCollector(observation.collectorId);
  }

  private recordEventTimings(entries: PerformanceEntryList): void {
    for (const entry of entries) {
      if (!isEventTimingEntry(entry)) {
        continue;
      }
      this.recentEventTimings.push({
        name: entry.name,
        startTime: entry.startTime,
        duration: entry.duration,
        processingStart: entry.processingStart,
        processingEnd: entry.processingEnd,
        interactionId: entry.interactionId,
      });
    }
    if (this.recentEventTimings.length > MAX_RECENT_EVENT_TIMINGS) {
      this.recentEventTimings.splice(
        0,
        this.recentEventTimings.length - MAX_RECENT_EVENT_TIMINGS
      );
    }
  }

  private drainPendingEventTimingEntries(): void {
    if (this.eventTimingObserver != null) {
      this.recordEventTimings(this.eventTimingObserver.takeRecords());
    }
  }
}
